import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./ReviewGuideBooking.css";
import NavUser from "../components/NavUser";
import PaymentService from "../services/PaymentService";
import { getCoinBalance } from "../services/coinService";
import { decodeToken } from "../services/userService";
import { startKokoTourPayment } from "../services/hotelbookingService";
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaCreditCard,
  FaUserFriends,
  FaLock
} from "react-icons/fa";

const ReviewGuideBooking = () => {
  const location = useLocation();
  const bookingDetails = location.state?.bookingDetails;

  const [tourData, setTourData] = useState(null);
  const [coinBalance, setCoinBalance] = useState(0);
  const [applyRewards, setApplyRewards] = useState(false);
  const [userId, setUserId] = useState(null);
  const [showPaymentSelect, setShowPaymentSelect] = useState(false);
  const [bookingResponse, setBookingResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("jwt_token");
    if (token) {
      const decoded = decodeToken(token);
      if (decoded && decoded.id) {
        setUserId(decoded.id);
      }
    }
    
    if (!userId) {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user && user.id) {
          setUserId(user.id);
        }
      }
    }
  }, [userId]);

  useEffect(() => {
    if (bookingDetails?.tourListingId) {
      const fetchTourDetails = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/guide-listing/${bookingDetails.tourListingId}`
          );
          setTourData(response.data);
        } catch (error) {
          console.error("Error fetching tour details:", error);
          toast.error("Failed to load tour details.");
        }
      };
      fetchTourDetails();
    }
  }, [bookingDetails]);

  useEffect(() => {
    if (userId) {
      const fetchCoinBalance = async () => {
        try {
          const balance = await getCoinBalance(userId);
          setCoinBalance(balance);
        } catch (error) {
          console.error("Error fetching coin balance:", error);
        }
      };
      fetchCoinBalance();
    }
  }, [userId]);

  const showToast = (message, type = "info") => {
    if (type === "success") toast.success(message);
    else if (type === "error") toast.error(message);
    else toast.info(message);
  };

  const getTourDiscountAmount = () => {
    if (!tourData || !tourData.discounts) return 0;
    const price = parseFloat(tourData.price) || 0;
    const discountPercent = parseFloat(tourData.discounts) || 0;
    return (price * discountPercent) / 100;
  };

  const getTotalPrice = () => {
    if (!tourData) return 0;
    const basePrice = parseFloat(tourData.price) || 0;
    const discount = getTourDiscountAmount();
    let total = basePrice - discount;

    if (applyRewards) {
      const coinValue = coinBalance;
      total -= coinValue;
    }

    return total > 0 ? total : 0;
  };

  const handleKokoPayment = async (bookingId, amount) => {
    try {
      const payload = {
        bookingId: bookingId,
        userId: userId,
        tourListingId: bookingDetails.tourListingId,
        numberOfGuests: bookingDetails.numberOfGuests,
        price: amount,
        bookingDate: bookingDetails.bookingDate,
        pickupRequested: bookingDetails.pickupRequested ? 1 : 0,
        specialRequests: bookingDetails.specialRequests,
        useCoinBalance: applyRewards ? 1 : 0,
        coins_used_amount: applyRewards ? coinBalance : 0
      };

      const data = await startKokoTourPayment(payload);
      if (!data || !data.actionUrl || !data.fields || Object.keys(data.fields).length === 0) {
        console.error("Koko start API returned invalid data:", data);
        showToast("Koko payment service is temporarily unavailable.", "error");
        return;
      }

      const { actionUrl, fields } = data;

      const form = document.createElement("form");
      form.method = "POST";
      form.action = actionUrl;

      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error("Koko payment error:", err);
      showToast("Koko payment failed to initialize.", "error");
    }
  };

  const handlePaymentsLkPayment = async (bookingId) => {
    try {
      setLoading(true);
      showToast("Redirecting to Payments.lk hosted checkout...", "info");

      const session = await PaymentService.createPaymentsLkCheckout({
        bookingType: "tour",
        bookingId: bookingId,
        clientType: "web",
      });

      if (session && session.checkoutUrl) {
        window.location.href = session.checkoutUrl;
      } else {
        showToast("Unable to start Payments.lk checkout session.", "error");
      }
    } catch (err) {
      console.error("Payments.lk payment error:", err);
      showToast(err.response?.data?.error || "Payments.lk payment failed to initialize.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmBooking = async () => {
    if (!bookingDetails || !bookingDetails.tourListingId) {
      showToast("Tour details missing. Cannot proceed.", "error");
      return;
    }
    if (!userId) {
      showToast("User not identified. Please log in.", "error");
      return;
    }

    setLoading(true);
    try {
      const totalPrice = getTotalPrice(); 
      const coinsUsedAmount = applyRewards ? coinBalance : 0;
      
      const payload = {
        booking_date: bookingDetails.bookingDate,
        pickup_requested: bookingDetails.pickupRequested ? 1 : 0,
        special_requests: bookingDetails.specialRequests,
        tour_listing_id: bookingDetails.tourListingId,
        payment_method_id: 3, 
        payment_status_id: 2, 
        user_id: userId,
        booking_status_id: 1, 
        price: totalPrice,
        number_of_guests: bookingDetails.numberOfGuests,
        use_coin_balance: applyRewards ? 1 : 0,
        coins_used_amount: coinsUsedAmount
      };

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/tour-bookings`, payload);
      
      if (response.data && response.data.id) {
        setBookingResponse(response.data);
        setShowPaymentSelect(true);
        showToast("Booking created! Please select payment method.", "success");
      } else {
        showToast("Failed to create booking.", "error");
      }
    } catch (err) {
      console.error("Guide booking creation error:", err);
      const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message;
      showToast("Error creating booking: " + errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!bookingDetails) {
    return (
      <div className="review-guide-booking-container">
        <NavUser />
        <div className="no-details-msg">No booking details found. Please go back.</div>
      </div>
    );
  }

  return (
    <div className="review-guide-booking-container">
      <NavUser />
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="review-content">
        <h1 className="page-title">Finalize Your Adventure</h1>
        <p className="page-subtitle">Review your details before confirming your guide booking.</p>

        <div className="review-grid">
          <div className="details-section">
            <div className="premium-card">
              <h2 className="section-title">Tour Information</h2>
              <div className="detail-row">
                <span className="detail-label">Tour Name</span>
                <span className="detail-value">{tourData?.title || "Loading..." }</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Guide Name</span>
                <span className="detail-value">{tourData?.guide?.user?.full_name || "Loading..."}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Booking Date</span>
                <span className="detail-value"><FaCalendarAlt style={{marginRight: '8px', color: '#3182ce'}} /> {new Date(bookingDetails.bookingDate).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </div>

            <div className="premium-card">
              <h2 className="section-title">Traveler Details</h2>
              <div className="detail-row">
                <span className="detail-label">Total Guests</span>
                <span className="detail-value"><FaUserFriends style={{marginRight: '8px', color: '#3182ce'}} /> {bookingDetails.numberOfGuests} Persons</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Pickup Service</span>
                <span className="detail-value"><FaMapMarkerAlt style={{marginRight: '8px', color: '#3182ce'}} /> {bookingDetails.pickupRequested ? "Requested" : "Not Required"}</span>
              </div>
              {bookingDetails.specialRequests && (
                <div className="detail-row" style={{flexDirection: 'column', gap: '8px'}}>
                  <span className="detail-label">Special Requests</span>
                  <span className="detail-value" style={{fontWeight: '400', fontSize: '14px', background: '#f8fafc', padding: '12px', borderRadius: '12px', width: '100%'}}>
                    {bookingDetails.specialRequests}
                  </span>
                </div>
              )}
            </div>

            <div className="premium-card rewards-card">
              <h2 className="section-title">Loyalty Rewards</h2>
              <div className="coin-balance">
                <div className="coin-icon">🪙</div>
                <div className="coin-info">
                  <span className="coin-label">Your Balance</span>
                  <span className="coin-amount">{coinBalance} Soloway Coins</span>
                </div>
              </div>
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={applyRewards}
                  onChange={(e) => setApplyRewards(e.target.checked)}
                  disabled={coinBalance <= 0}
                />
                <span className="checkbox-text">Apply rewards to get instant discount (1 Coin = 1 LKR)</span>
              </label>
            </div>
          </div>

          <div className="summary-section">
            {!showPaymentSelect ? (
              <div className="price-summary-card">
                <h2 className="section-title">Price Breakdown</h2>
                <div className="summary-row">
                  <span>Base Price</span>
                  <span>Rs. {parseFloat(tourData?.price || 0).toLocaleString()}</span>
                </div>
                
                {getTourDiscountAmount() > 0 && (
                  <div className="summary-row discount">
                    <span>Seasonal Discount</span>
                    <span>-Rs. {getTourDiscountAmount().toLocaleString()}</span>
                  </div>
                )}

                {applyRewards && (
                  <div className="summary-row reward">
                    <span>Rewards Applied</span>
                    <span>-Rs. {coinBalance.toLocaleString()}</span>
                  </div>
                )}

                <div className="summary-divider"></div>

                <div className="total-row">
                  <span className="total-label">Total Payable</span>
                  <span className="total-amount">Rs. {getTotalPrice().toLocaleString()}</span>
                </div>

                <button 
                  className="confirm-btn" 
                  onClick={handleConfirmBooking}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Confirm & Proceed to Payment"}
                </button>
                <div className="secure-badge">
                  <FaLock /> 256-bit Secure Payment Processing
                </div>
              </div>
            ) : (
              <div className="payment-select-card animate-fade-in">
                <div className="premium-card">
                  <div className="payment-header">
                    <h2 className="section-title">Select Payment Method</h2>
                    <p style={{fontSize: '14px', color: '#718096'}}>Order Reference: <strong>#TB-{bookingResponse?.id}</strong></p>
                  </div>

                  <div className="payment-grid">
                    <div className="payment-option" onClick={() => handlePaymentsLkPayment(bookingResponse.id)}>
                      <div className="option-icon"><FaCreditCard /></div>
                      <div className="option-info">
                        <span className="option-title">Online Payment (Cards / Wallets)</span>
                        <span className="option-desc">Official Payments.lk Hosted Gateway</span>
                      </div>
                    </div>

                    <div className="payment-option" onClick={() => handleKokoPayment(bookingResponse.id, getTotalPrice())}>
                      <div className="option-icon" style={{ background: '#000', color: '#fff', fontWeight: '900' }}>K.</div>
                      <div className="option-info">
                        <span className="option-title">Koko</span>
                        <span className="option-desc">Split into 3 interest-free payments</span>
                      </div>
                    </div>
                  </div>

                  <div className="payment-summary">
                    <div className="detail-row" style={{border: 'none', padding: '0'}}>
                      <span className="detail-label">Amount Due</span>
                      <span className="total-amount" style={{fontSize: '22px'}}>Rs. {getTotalPrice().toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewGuideBooking;
